import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import { graphql, useStaticQuery } from "gatsby"
import {
  articleMetadata,
  normaliseArticlePath,
  breadcrumbsFor,
} from "../data/article-metadata"
import shareImage from "../images/hero-image.jpg"

const siteMetadata = require("../data/site-metadata.json")

interface Props {
  title: string
  description?: string
  lang?: string
  meta?: React.ComponentProps<typeof Helmet>["meta"]
  noindex?: boolean
}

function SEO({
  description,
  lang = "en",
  meta = [],
  title,
  noindex = false,
}: Props) {
  const { pathname } = useLocation()
  const baseUrl = siteMetadata.siteUrl
  const pagePath =
    pathname === "/" || /\.[a-z]+$/i.test(pathname)
      ? pathname
      : `${pathname.replace(/\/+$/, "")}/`
  const canonical = new URL(pagePath, baseUrl).href
  const article = noindex
    ? undefined
    : articleMetadata[normaliseArticlePath(pathname)]
  const images = useStaticQuery(graphql`
    query ArticleSocialImages {
      allFile(filter: { sourceInstanceName: { eq: "images" } }) {
        nodes {
          relativePath
          publicURL
        }
      }
    }
  `)
  const asset =
    article &&
    images.allFile.nodes.find(
      (file: { relativePath: string; publicURL: string }) =>
        file.relativePath === article.image
    )
  const imageUrl = new URL(asset ? asset.publicURL : shareImage, baseUrl).href
  const imageAlt = article
    ? `Illustration for ${article.title}`
    : "School Calendar visual weekly planning"
  const metaDescription = description || siteMetadata.description
  const fullTitle = `${title} | ${siteMetadata.title}`
  const publisher = {
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    name: siteMetadata.title,
    url: baseUrl,
    sameAs: siteMetadata.social.map((profile: { url: string }) => profile.url),
  }
  const structuredData = article
    ? {
        "@context": "https://schema.org",
        "@graph": [
          publisher,
          {
            "@type": "Article",
            "@id": `${canonical}#article`,
            headline: article.title,
            description: metaDescription,
            url: canonical,
            mainEntityOfPage: canonical,
            image: imageUrl,
            publisher: { "@id": publisher["@id"] },
            ...(article.author
              ? {
                  author: {
                    "@type": article.author.type,
                    name: article.author.name,
                    url: new URL(article.author.url, baseUrl).href,
                  },
                }
              : {}),
            ...(article.published ? { datePublished: article.published } : {}),
            ...(article.modified ? { dateModified: article.modified } : {}),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbsFor(article).map((crumb, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: crumb.name,
              item: new URL(crumb.url, baseUrl).href,
            })),
          },
        ],
      }
    : pathname === "/" && !noindex
    ? { "@context": "https://schema.org", ...publisher }
    : null

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={fullTitle}
      link={noindex ? [] : [{ rel: "canonical", href: canonical }]}
      meta={[
        { name: "msvalidate.01", content: "D7062B167634B16F9F04CCC15D0D9FAB" },
        { name: "description", content: metaDescription },
        {
          name: "robots",
          content: noindex
            ? "noindex, follow"
            : "index, follow, max-image-preview:large",
        },
        { property: "og:site_name", content: siteMetadata.title },
        { property: "og:title", content: fullTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: article ? "article" : "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: imageUrl },
        {
          property: "og:image:alt",
          content: imageAlt,
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: fullTitle },
        { name: "twitter:description", content: metaDescription },
        { name: "twitter:image", content: imageUrl },
        {
          name: "twitter:image:alt",
          content: imageAlt,
        },
        ...meta,
      ]}
    >
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData).replace(/</g, "\\u003c")}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
